import { isAxiosError, type AxiosError } from 'axios';

import type { InstallationRiskApi } from './installation-risk-api.types';

import http from './installation-risk-api.http';

export { default as installationRiskHttp } from './installation-risk-api.client';
export * from './installation-risk-api.http';
export { default as installationRiskService } from './installation-risk-api.service';

export class InstallationApprovalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InstallationApprovalError';
    Object.setPrototypeOf(this, InstallationApprovalError.prototype);
  }
}

const formatInstallationApprovalError = (error: AxiosError): string => {
  const status = error?.response?.status;
  const responseData = error?.response?.data as undefined | { detail?: string; message?: string };
  const detail = responseData?.detail;
  const message = responseData?.message;

  const errorMap = {
    400: (errorDetail?: string, errorMessage?: string) => {
      if (errorDetail?.includes('status')) {
        return 'A instalação não está em um status válido para aprovação manual. '
          + 'Verifique se ela está aguardando confirmação do cliente.';
      }
      return errorDetail || errorMessage || 'Requisição inválida. Verifique os dados e tente novamente.';
    },
    401: 'Sua sessão expirou ou você não está autenticado. '
      + 'Por favor, faça login novamente e tente outra vez.',
    403: (errorDetail?: string) => {
      if (errorDetail?.includes('automatic attempts')) {
        return 'Esta funcionalidade só está disponível após 3 tentativas de confirmação automática falhadas. '
          + 'Por favor, aguarde o processo automático tentar enviar a confirmação pelo menos 3 vezes '
          + 'antes de usar a aprovação manual.';
      }
      return 'Você não tem permissão para aprovar instalações manualmente. '
        + 'Verifique se você pertence ao grupo correto no sistema.';
    },
    404: 'Instalação não encontrada. '
      + 'A instalação pode ter sido removida ou o ID está incorreto.',
    412: 'Nem todos os inversores instalados estão aguardando confirmação do cliente. '
      + 'Verifique o status dos inversores antes de aprovar a instalação.',
    413: 'Os arquivos anexados são muito grandes. '
      + 'Por favor, reduza o tamanho dos arquivos ou envie menos arquivos por vez.',
    422: (errorDetail?: string, errorMessage?: string) => {
      if (errorDetail?.includes('evidence')) {
        return 'É obrigatório anexar pelo menos um arquivo de evidência do aceite do cliente.';
      }
      return errorDetail || errorMessage || 'Dados inválidos. Verifique os campos e tente novamente.';
    },
  };

  if (status && status in errorMap) {
    const errorHandler = errorMap[status as keyof typeof errorMap];
    return typeof errorHandler === 'function' ? errorHandler(detail, message) : errorHandler;
  }

  if (status && status >= 500) {
    return 'Erro interno do servidor. Por favor, tente novamente em alguns minutos. '
      + 'Se o problema persistir, contate o suporte técnico.';
  }

  return message || detail || error.message
    || 'Erro inesperado ao aprovar instalação. Por favor, tente novamente.';
};

export default {
  getPartnerMigrationProjects: async ({
    manufacture_platform,
    page = 1,
    pageSize = 20,
    partnerId,
  }: InstallationRiskApi.GetMigrationProjectsParams) => {
    const res = await http.get<InstallationRiskApi.GetMigrationProjectsResponse>(
      `/installations/partners/${partnerId}/ping-migration-projects`,
      { params: { manufacture_platform, page, pageSize } },
    );

    return res.data;
  },

  bypassCustomerConfirmation: async (
    projectId: string,
    data: InstallationRiskApi.BypassCustomerConfirmationData,
  ): Promise<void> => {
    const formData = new FormData();

    formData.append('has_phone_change', data.hasPhoneChange.toString());

    if (data.hasPhoneChange && data.newPhoneNumber) {
      formData.append('new_phone_number', data.newPhoneNumber);
    }

    if (data.zendeskTicket) {
      formData.append('zendesk_ticket', data.zendeskTicket);
    }

    if (data.notes) {
      formData.append('notes', data.notes);
    }

    data.customerEvidenceFiles.forEach((file) => {
      formData.append('evidence_files', file);
    });

    try {
      await http.post(
        `/projects/${projectId}/bypass-customer-confirmation`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
    } catch (error) {
      if (isAxiosError(error)) {
        const formattedMessage = formatInstallationApprovalError(error);
        throw new InstallationApprovalError(formattedMessage);
      }
      throw new InstallationApprovalError(
        'Erro inesperado ao aprovar instalação. Por favor, tente novamente.',
      );
    }
  },
};
